import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import { api, type AppCard as Card, type AppCardImage, type AppCardItem, type AppMessageView } from '../../api/client';
import { fullWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { barColor, cardLabel, fieldRows, markdownBlocks, markdownInline, shownImage, statusWord, type MdNode } from '../../lib/appcard';
import { CodeBlock } from './CodeBlock';
import { FileViewer, type ViewerFile } from './FileViewer';

/**
 * A webhook's message (1.12.19): its own text, then a card per attachment or group of blocks: a
 * coloured bar with the status in words (never colour alone), the title as a link, text, fields two
 * to a row, pictures (through Stitch, never the sender's site), links, a footer and a time. Text is
 * Stitch's Markdown (lib/appcard.ts), shown as React text, never as HTML.
 */

const linkProps = (url: string) => (url.toLowerCase().startsWith('mailto:') ? { href: url } : { href: url, target: '_blank', rel: 'noopener noreferrer nofollow' });

/** Markdown's words as React. */
export function MdInline({ nodes }: { nodes: MdNode[] }) {
  return (
    <>
      {nodes.map((n, i) =>
        n.t === 'text' ? (
          <Fragment key={i}>{n.text}</Fragment>
        ) : n.t === 'code' ? (
          <code key={i} className="msg-code-inline">
            {n.code}
          </code>
        ) : n.t === 'link' ? (
          <a key={i} {...linkProps(n.url)}>
            <MdInline nodes={n.children} />
          </a>
        ) : n.t === 'strong' ? (
          <strong key={i}>
            <MdInline nodes={n.children} />
          </strong>
        ) : n.t === 'em' ? (
          <em key={i}>
            <MdInline nodes={n.children} />
          </em>
        ) : (
          <del key={i}>
            <MdInline nodes={n.children} />
          </del>
        )
      )}
    </>
  );
}

/** Markdown as blocks: paragraphs, quotes, headings and code. */
export function Markdown({ text, className = 'msg-md' }: { text: string; className?: string }) {
  const blocks = markdownBlocks(text);
  if (!blocks.length) return null;
  return (
    <div className={className}>
      {blocks.map((b, i) =>
        b.t === 'code' ? (
          <CodeBlock key={i} code={b.code} lang={b.lang} />
        ) : b.t === 'quote' ? (
          <blockquote key={i} className="msg-md-quote">
            <MdInline nodes={b.nodes} />
          </blockquote>
        ) : b.t === 'h' ? (
          <p key={i} className="msg-md-h">
            <MdInline nodes={b.nodes} />
          </p>
        ) : (
          <p key={i} className="msg-md-p">
            <MdInline nodes={b.nodes} />
          </p>
        )
      )}
    </div>
  );
}

interface Ctx {
  domain: string;
  conversationId: string;
  messageId: string;
  app: AppMessageView;
  open: (n: number, name: string) => void;
}

/** A picture: through Stitch once fetched; a placeholder while it comes; else a link to where it is. */
function Picture({ ctx, img, small, title }: { ctx: Ctx; img: AppCardImage; small?: boolean; title?: string }) {
  const shown = shownImage(ctx.app, img);
  const alt = img.alt || title || 'Picture';
  if (shown.kind === 'picture') {
    return (
      <button type="button" className={`msg-card-pic${small ? ' small' : ''}`} aria-label={`Open the picture: ${alt}`} onClick={() => ctx.open(shown.n, alt)}>
        <img src={api.appImageUrl(ctx.domain, ctx.conversationId, ctx.messageId, shown.n, shown.version)} alt={alt} loading="lazy" decoding="async" />
      </button>
    );
  }
  if (shown.kind === 'pending') return <span className={`msg-card-pic pending${small ? ' small' : ''}`} role="img" aria-label={`${alt} (loading)`} />;
  if (!shown.url) return null;
  return (
    <a className="msg-card-piclink" {...linkProps(shown.url)} title={shown.url}>
      <Icon name="link" size={13} /> Picture: {shown.label}
    </a>
  );
}

function Item({ ctx, item }: { ctx: Ctx; item: AppCardItem }): ReactNode {
  switch (item.t) {
    case 'header':
      return (
        <p className="msg-card-header">
          <MdInline nodes={markdownInline(item.text)} />
        </p>
      );
    case 'text':
      return <Markdown text={item.text} className="msg-md msg-card-text" />;
    case 'context':
      return <Markdown text={item.text} className="msg-md msg-card-context" />;
    case 'divider':
      return <hr className="msg-card-divider" />;
    case 'fields':
      return (
        <dl className="msg-card-fields">
          {fieldRows(item.fields).map((row, r) => (
            <div key={r} className={`msg-card-row${row.length === 2 ? ' pair' : ''}`}>
              {row.map((f, i) => (
                <div key={i} className="msg-card-field">
                  {f.name ? (
                    <dt>
                      <MdInline nodes={markdownInline(f.name)} />
                    </dt>
                  ) : null}
                  <dd>
                    <Markdown text={f.value} />
                  </dd>
                </div>
              ))}
            </div>
          ))}
        </dl>
      );
    case 'image':
      return (
        <figure className={`msg-card-figure${item.small ? ' small' : ''}`}>
          <Picture ctx={ctx} img={item} {...(item.small ? { small: true } : {})} {...(item.title ? { title: item.title } : {})} />
          {item.title && (
            <figcaption>
              <MdInline nodes={markdownInline(item.title)} />
            </figcaption>
          )}
        </figure>
      );
    case 'links':
      return (
        <div className="msg-card-links">
          {item.links.map((l, i) => (
            <a key={i} className="msg-card-button" {...linkProps(l.url)}>
              <MdInline nodes={markdownInline(l.text)} />
              <Icon name="arrowOut" size={13} />
            </a>
          ))}
        </div>
      );
  }
}

function OneCard({ ctx, card }: { ctx: Ctx; card: Card }) {
  const word = statusWord(card.status);
  const bar = barColor(card);
  return (
    <>
      {card.pretext && <Markdown text={card.pretext} className="msg-md msg-card-pretext" />}
      <section className={`msg-card${card.status ? ` ${card.status}` : ''}${bar ? ' colored' : ''}`} style={bar ? ({ '--msg-card-bar': bar } as CSSProperties) : undefined} aria-label={cardLabel(card)}>
        <div className="msg-card-main">
          {(word || card.author) && (
            <div className="msg-card-top">
              {word && (
                <span className={`msg-card-status ${card.status}`}>
                  <Icon name={card.status === 'ok' ? 'check' : card.status === 'info' ? 'info' : 'alert'} size={12} strokeWidth={2.6} />
                  {word}
                </span>
              )}
              {card.author && (
                <span className="msg-card-author">
                  {card.author.url ? (
                    <a {...linkProps(card.author.url)}>
                      <MdInline nodes={markdownInline(card.author.name)} />
                    </a>
                  ) : (
                    <MdInline nodes={markdownInline(card.author.name)} />
                  )}
                </span>
              )}
            </div>
          )}
          {card.title && (
            <p className="msg-card-title">
              {card.title.url ? (
                <a {...linkProps(card.title.url)}>
                  <MdInline nodes={markdownInline(card.title.text)} />
                </a>
              ) : (
                <MdInline nodes={markdownInline(card.title.text)} />
              )}
            </p>
          )}
          {card.items.map((it, i) => (
            <Item key={i} ctx={ctx} item={it} />
          ))}
          {(card.footer || card.time) && (
            <p className="msg-card-foot">
              {card.footer && <MdInline nodes={markdownInline(card.footer)} />}
              {card.footer && card.time && <span aria-hidden="true"> · </span>}
              {card.time && (
                <time dateTime={card.time} title={fullWhen(card.time)}>
                  {fullWhen(card.time)}
                </time>
              )}
            </p>
          )}
        </div>
        {card.thumb && (
          <div className="msg-card-thumb">
            <Picture ctx={ctx} img={card.thumb} small />
          </div>
        )}
      </section>
    </>
  );
}

/** A webhook's message: its text and its cards. */
export function AppMessage({ domain, conversationId, messageId, app, from }: { domain: string; conversationId: string; messageId: string; app: AppMessageView; from: { name: string; at: string } }) {
  const [viewing, setViewing] = useState<{ files: ViewerFile[]; start: number } | null>(null);
  const pictures: ViewerFile[] = app.images
    .filter((x) => x.status === 'ok' && x.version)
    .map((x) => ({ id: `img-${x.n}`, name: `Picture ${x.n + 1}`, contentType: 'image/png', bytes: null, url: api.appImageUrl(domain, conversationId, messageId, x.n, x.version!), picture: true }));
  const ctx: Ctx = {
    domain,
    conversationId,
    messageId,
    app,
    open: (n) => setViewing({ files: pictures, start: Math.max(0, pictures.findIndex((p) => p.id === `img-${n}`)) })
  };
  return (
    <div className="msg-app">
      {app.text && <Markdown text={app.text} />}
      {app.cards.map((c, i) => (
        <OneCard key={i} ctx={ctx} card={c} />
      ))}
      {viewing && <FileViewer files={viewing.files} start={viewing.start} from={from} onClose={() => setViewing(null)} />}
    </div>
  );
}
